<template>
  <nav class="top-nav">
    <div class="nav-left">
      <div class="agents-menu-wrapper" ref="agentsMenuRef">
        <button 
          class="agents-button"
          @click="handleToggleAgentsMenu"
          aria-haspopup="true"
          :aria-expanded="agentsMenuOpen"
        >
          Agents
          <svg 
            width="12" 
            height="12" 
            viewBox="0 0 12 12" 
            fill="currentColor"
            class="dropdown-icon"
            :class="{ rotated: agentsMenuOpen }"
          >
            <path d="M2 4l4 4 4-4H2z"/>
          </svg>
        </button>
        <div v-if="agentsMenuOpen" class="agents-dropdown-menu">
          <router-link 
            :to="{ name: 'home' }" 
            class="dropdown-item"
            :class="{ disabled: isOnLogin }"
            @click="closeAgentsMenu"
            active-class="router-link-active"
            exact-active-class="router-link-exact-active"
            :aria-disabled="isOnLogin ? 'true' : 'false'"
          >
            <span class="dropdown-label">Home</span>
          </router-link>
          <router-link 
            :to="{ name: 'settings' }" 
            class="dropdown-item"
            :class="{ disabled: isOnLogin }"
            @click="closeAgentsMenu"
            active-class="router-link-active"
            exact-active-class="router-link-exact-active"
            :aria-disabled="isOnLogin ? 'true' : 'false'"
          >
            <span class="dropdown-label">Settings</span>
          </router-link>
          <div class="dropdown-divider" aria-hidden="true"></div>
          <button 
            class="dropdown-item dropdown-button"
            @click="handleCreateAgent"
            :class="{ disabled: isOnLogin }"
            :aria-disabled="isOnLogin ? 'true' : 'false'"
            :tabindex="isOnLogin ? -1 : 0"
          >
            <span class="dropdown-label">Create Agent</span>
          </button>
        </div>
      </div>
    </div>
    <div class="nav-right">
      <button v-if="!isAuthenticated" @click="login">Sign In</button>
      <div v-else class="user-menu-wrapper" ref="userMenuRef">
        <button
          class="avatar-btn"
          @click="handleToggleUserMenu"
          aria-haspopup="true"
          :aria-expanded="userMenuOpen"
        >
          <span v-if="userPicture" class="avatar-img">
            <img :src="userPicture" alt="User avatar" />
          </span>
          <span v-else-if="userInitials" class="avatar-initials">{{ userInitials }}</span>
          <span v-else class="avatar-icon">👤</span>
        </button>
        <div v-if="userMenuOpen" class="dropdown-menu" tabindex="-1">
          <div class="dropdown-header">
            <div class="dropdown-avatar">
              <span v-if="userPicture">
                <img :src="userPicture" alt="User avatar" />
              </span>
              <span v-else-if="userInitials" class="avatar-initials">{{ userInitials }}</span>
              <span v-else class="avatar-icon">👤</span>
            </div>
            <div class="dropdown-user-info">
              <div class="dropdown-name">{{ userDisplayName }}</div>

              <div class="dropdown-email">{{ userEmail }}</div>
              <span
                class="status-indicator"
                :title="statusTooltip"
                :style="{
                  color: statusColor,
                  marginLeft: 0,
                  marginTop: '0.5em',
                  fontSize: '1.2em',
                }"
                aria-label="Connection status"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  :style="{
                    transform: 'rotate(90deg)',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                  }"
                  aria-hidden="true"
                >
                  <path d="M10 0L3 11h5v9l7-11h-5z" />
                </svg>
              </span>
            </div>
          </div>
          <div class="dropdown-actions">
            <button @click="onSettings">Settings</button>
            <button v-if="isDev" @click="runDevelopmentSmokeTestHandler">Run Auth Smoke Test</button>
            <button @click="logout">Sign Out</button>
          </div>
        </div>
      </div>
    </div>
  </nav>
  <DevelopmentSmokeResultsModal
    :open="showDevelopmentSmokeTestModal"
    :result="developmentSmokeTestResult"
    @update:open="onUpdateDevelopmentSmokeOpen"
    @close="onCloseDevelopmentSmokeModal"
  />
  <AddPanelModal v-if="showAddPanelModal" @close="closeAddPanelModal" />
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useAuth } from "@/composables/useAuth";
import { useMenuBehavior } from "@/composables/useMenuBehavior";
import { fetchStatus, fetchConfig } from "@/services/api";
import DevelopmentSmokeResultsModal from "@/components/DevelopmentSmokeResultsModal.vue";
import AddPanelModal from "@/components/AddPanelModal.vue";
import { runDevelopmentSmokeTest } from "@/composables/developmentSmokeTest";

function getFirstScope(obj: unknown): string | undefined {
  if (!obj || typeof obj !== "object") return undefined;
  const o = obj as Record<string, unknown>;
  const scopes = o.scopes;
  if (Array.isArray(scopes) && scopes.length > 0 && typeof scopes[0] === "string") return scopes[0] as string;
  return undefined;
}

const router = useRouter();
const { isAuthenticated, user, login, logout } = useAuth();

// Detect whether current route is the login page
const isOnLogin = computed(() => router.currentRoute.value.name === "login");

// Menu behavior setup
const userMenuRef = ref<HTMLElement | null>(null);
const { isOpen: userMenuOpen, toggleMenu: toggleUserMenu, closeMenu: closeUserMenu } = useMenuBehavior({
  menuContainerRef: userMenuRef
});

// Agents menu behavior setup
const agentsMenuRef = ref<HTMLElement | null>(null);
const { isOpen: agentsMenuOpen, toggleMenu: toggleAgentsMenu, closeMenu: closeAgentsMenu } = useMenuBehavior({
  menuContainerRef: agentsMenuRef
});

// AddPanelModal state
const showAddPanelModal = ref(false);

// detect local dev based on NODE_ENV
const isDev = process.env.NODE_ENV === "development";

const status = ref<"pending" | "ok" | "error">("pending");

// development smoke test modal state
const showDevelopmentSmokeTestModal = ref(false);
const developmentSmokeTestResult = ref("");

function onUpdateDevelopmentSmokeOpen(v: boolean) {
  showDevelopmentSmokeTestModal.value = v;
}

function onCloseDevelopmentSmokeModal() {
  showDevelopmentSmokeTestModal.value = false;
}

async function runDevelopmentSmokeTestHandler() {
  developmentSmokeTestResult.value = "Running...";
  showDevelopmentSmokeTestModal.value = true;
  // Prefer server-provided config for validation
  try {
    const cfg = await fetchConfig();
    const firstScope = getFirstScope(cfg);
    developmentSmokeTestResult.value = await runDevelopmentSmokeTest({
      expectedIss: `https://login.microsoftonline.com/${cfg.tenantId}`,
      expectedAud: cfg.clientId,
      expectedScope: firstScope,
    });
  } catch (e) {
    // fallback to build-time envs
    developmentSmokeTestResult.value = await runDevelopmentSmokeTest({
      expectedIss: process.env.VUE_APP_AUTHORITY || process.env.VUE_APP_AUTH_ISSUER,
      expectedAud: process.env.VUE_APP_API_CLIENT_ID || process.env.VUE_APP_API_AUDIENCE,
      expectedScope: process.env.VUE_APP_API_SCOPE,
    });
  }
}

async function checkStatusOnce() {
  status.value = "pending";
  try {
    const result = await fetchStatus();
    status.value = result === "ok" ? "ok" : "error";
  } catch {
    status.value = "error";
  }
}

// Override toggleUserMenu to include status check
function handleToggleUserMenu() {
  toggleUserMenu();
  if (userMenuOpen.value) {
    checkStatusOnce();
    nextTick(() => {
      // Focus the dropdown for accessibility
      const el = document.querySelector(".dropdown-menu");
      if (el) (el as HTMLElement).focus();
    });
  }
}

function onSettings() {

  router.push({ name: "settings" });
  // Close the user menu via the composable (avoid referencing undefined `menuOpen`)
  closeUserMenu();
}

// Agents menu functions
function handleToggleAgentsMenu() {
  toggleAgentsMenu();
}

function handleCreateAgent() {
  closeAgentsMenu();
  // Navigate to home first, then show the dialog
  router.push({ name: "home" }).then(() => {
    showAddPanelModal.value = true;
  });
}

function closeAddPanelModal() {
  showAddPanelModal.value = false;
}

const userDisplayName = computed(() => user.value?.name || user.value?.username || "User");
const userEmail = computed(() => user.value?.username || "");
const userPicture = computed<string | null>(() => {
  const pic = user.value?.idTokenClaims?.picture;
  return typeof pic === "string" ? pic : null;
});
const userInitials = computed(() => {
  const name = user.value?.name || "";
  if (!name) return null;
  const parts = name.split(" ");
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || null;
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
});

const statusColor = computed(() => {
  if (status.value === "ok") return "#27c840";
  if (status.value === "pending") return "#bbb";
  return "#c00";
});
const statusTooltip = computed(() => {
  if (status.value === "ok") return "Connected";
  if (status.value === "pending") return "Checking...";
  return "Not connected";
});
</script>

<style scoped>
.top-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: #FFFFFF;
  border-bottom: 1px solid #E5E5E5;
  box-shadow: 0px 1px 3px rgba(0,0,0,0.04);
}

.nav-left {
  display: flex;
  align-items: center;
}

.app-logo {
  width: 32px;
  height: 32px;
  background: transparent;
}

/* Agents menu styles */
.agents-menu-wrapper {
  position: relative;
}

.agents-button {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #333333;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s ease-in-out;
}

.agents-button:hover {
  background-color: #F9FAFB;
}

.agents-button:focus {
  outline: none;
  /* Soft focus ring instead of a harsh solid outline */
  box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.10);
  border-radius: 8px;
}

.dropdown-icon {
  transition: transform 0.2s ease-in-out;
}

.dropdown-icon.rotated {
  transform: rotate(180deg);
}

.agents-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 160px;
  background: #FFFFFF;
  border: 1px solid #E5E5E5;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  z-index: 100;
  padding: 8px 0;
  margin-top: 4px;
}


.dropdown-item {
  display: flex;
  width: 100%;
  align-items: center;
  padding: 0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #374151;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
}

.dropdown-item:hover {
  background: #F8FAFF;
}

.dropdown-item:focus {
  outline: none;
  background: #F8FAFF;
  box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.08);
}

.dropdown-label {
  display: block;
  padding: 10px 16px;
  width: 100%;
  text-align: left;
}

.dropdown-item:focus .dropdown-label {
  outline: none;
  box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.08);
  background: #F8FAFF;
}

.dropdown-label {
  display: inline-block;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out;
}

.dropdown-item.router-link-active,
.dropdown-item.router-link-exact-active {
  background: #EEF2FF; /* very light indigo/blue */
  color: #2563EB; /* primary, slightly deeper and more modern blue */
  font-weight: 600;
}

.dropdown-divider {
  height: 1px;
  margin: 6px 8px;
  background: linear-gradient(90deg, rgba(0,0,0,0.06), rgba(0,0,0,0));
  border-radius: 2px;
}

.dropdown-button {
  width: 100%;
}

/* Disabled state for menu items when on login page */
.dropdown-item.disabled {
  color: #9CA3AF;
  cursor: default;
  pointer-events: none;
}

.dropdown-item.disabled:focus {
  outline: none;
  background: none;
}

.nav-right {
  display: flex;
  align-items: center;
}

.nav-right button {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(180deg, #2563EB 0%, #1E40AF 100%);
  color: #FFFFFF;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.14);
}

.nav-right button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(37, 99, 235, 0.16);
}

.nav-right button:focus {
  outline: none;
  box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.12);
}

.avatar-btn {
  background: none !important;
  border: none !important;
  padding: 0;
  margin-left: 12px;
  cursor: pointer;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  max-width: 36px;
  max-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  transition: box-shadow 0.2s ease-in-out;
  aspect-ratio: 1;
  flex-shrink: 0;
}

.avatar-btn:hover {
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
}

.avatar-btn:focus {
  outline: none;
  box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.10);
}

.avatar-img img {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-initials,
.avatar-icon {
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  max-width: 36px;
  max-height: 36px;
  border-radius: 50%;
  background: #6B7280;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.5px;
  aspect-ratio: 1;
  flex-shrink: 0;
}

.user-menu-wrapper {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  right: 0;
  top: 44px;
  min-width: 220px;
  background: #FFFFFF;
  border: 1px solid #E5E5E5;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  z-index: 100;
  padding: 8px 0;
  outline: none;
}

.dropdown-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #F3F4F6;
}

.dropdown-avatar {
  margin-right: 12px;
}

.dropdown-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.dropdown-avatar .avatar-initials,
.dropdown-avatar .avatar-icon {
  width: 40px;
  height: 40px;
  font-size: 16px;
}

.dropdown-user-info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.dropdown-name {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #111827;
  margin-bottom: 2px;
}

.dropdown-email {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #6B7280;
  margin-bottom: 8px;
}

.dropdown-actions {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}

.dropdown-actions button {
  background: none;
  border: none;
  text-align: left;
  padding: 12px 16px;
  cursor: pointer;
  color: #374151;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  transition: background-color 0.15s ease-in-out;
}

.dropdown-actions button:hover {
  background: #F9FAFB;
}

.dropdown-actions button:focus {
  outline: 2px solid #0078D4;
  outline-offset: -2px;
  background: #F9FAFB;
}

.status-indicator {
  font-size: 1.2rem;
  transition: color 0.2s;
  vertical-align: middle;
}
</style>
